import React from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import Head from 'next/head';
import { Grid, Box, Container } from '@mui/material';
import { GetServerSidePropsContext } from 'next';
import Bugsnag from '@bugsnag/js';
import { useI18nContext } from '@procore/core-react';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import BrowseByTrade from '../../components/Trades/BrowseByTrade';
import {
  convertUCSBusinessToBusiness,
  fetchBusinessFromGraphql,
  fetchMetricsFromGraphql,
} from '../../data/business-registry/business';
import {
  getMonolithCompany,
  isPayingAccount,
} from '../../data/monolith/company';
import { Business } from '../../types/business';
import GoogleLocalBusinessScript from '../../components/Google/GoogleLocalBusinessScript';
import {
  getInitialFilterState,
  getValidFiltersFromQuery,
} from '../../helpers/filters';
import { SearchRequest } from '../../data/search';
import { canonicalUrl } from '../../helpers/canonical-url';
import { GeoInfo, getGeoInfoFromBusiness } from '../../services/geo-info';
import { Metrics } from '../../types/metrics-types';
import { useStyles } from './[id].styles';
import { usePageView } from '../../components/Analytics/utilities';
import BusinessProfileLayout from '../../components/Business/BusinessProfileLayout';
import { absoluteUrl } from '../../helpers/absolute-url';
import { getLocationBreadcrumbs } from '../../helpers/location-breadcrumbs';
import { BreadcrumbItem } from '../../components/Breadcrumbs/BreadcrumbContainer';
import GoogleBreadcrumbScript from '../../components/Google/GoogleBreadcrumbScript';
import {
  DEFAULT_SEARCH_RADIUS,
  DEFAULT_SEARCH_RADIUS_KM_TO_MI,
} from '../../constants';
import { getSearchResults } from '../../services/search';
import { paginationDefaults } from '../../types/pagination';
import DistanceUnit from '../../data/location/distance-units';
import { getFlagsValues } from '../../components/LaunchDarkly/LaunchDarklyClient';
import { logFetch } from '../../shared/log';
import { fetchFromCache } from '../../services/redis';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const flags = await getFlagsValues(context.req, {
    'claim-business-profile': true,
    'ad-banner': 'A',
    'show-licenses': false,
  });
  const claimBusinessProfile = flags['claim-business-profile'];
  const adBanner = flags['ad-banner'];
  const showLicenses = flags['show-licenses'];
  const { req } = context;
  const { origin } = absoluteUrl(req);

  const { id } = context.query;
  const workingId = id.toString();

  let business: Business = await fetchBusinessFromGraphql(workingId);
  if (!business) {
    return {
      notFound: true,
    };
  }
  if (business.primarySlug !== id) {
    return {
      redirect: {
        destination: `${publicRuntimeConfig.basePath}/p/${business.primarySlug}`,
        permanent: true,
      },
    };
  }

  if (business.companyId) {
    const cacheKey = `procore:pcn:business:paying_account:${business.id}`;
    await Promise.all([
      getMonolithCompany(business.companyId).catch((e) => {
        const message = `getMonolithCompany failed for company ${business.companyId}`;
        Bugsnag.notify(e);
        logFetch(message, JSON.stringify(e));
        return null;
      }),
      fetchFromCache(cacheKey).catch((e) => {
        const message = `fetchFromCache failed for key ${cacheKey}`;
        Bugsnag.notify(e);
        logFetch(message, JSON.stringify(e));
        return null;
      }),
    ]).then(([companyResponse, isPayingAccountResponse]) => {
      business = {
        ...business,
        monolithCreatedAt: companyResponse?.created_at ?? null,
        payingAccount:
          isPayingAccountResponse?.paying_account !== undefined
            ? !!isPayingAccountResponse.paying_account
            : null,
      };
    });
  }

  if (business.companyId && business.payingAccount === null) {
    isPayingAccount(business.companyId)
      .then((isPayingAccountResponse) => {
        business = {
          ...business,
          payingAccount: isPayingAccountResponse
            ? !!isPayingAccountResponse.paying_account
            : true,
        };
      })
      .catch((e) => {
        const message = `isPayingAccount failed for company ${business.companyId}`;
        Bugsnag.notify(e);
        logFetch(message, JSON.stringify(e));
        business = {
          ...business,
          payingAccount: false,
        };
      });
  }

  const workingQuery = {
    trades: business.providedServices.map((trade) => trade.key).join(','),
  };
  const validFilters = await getValidFiltersFromQuery(
    workingQuery,
    context.locale
  );
  const filters = getInitialFilterState(validFilters, false, []);
  const geoInfo = getGeoInfoFromBusiness(business);
  const metrics =
    (business.visibility?.showProcoreActivity &&
      (await fetchMetricsFromGraphql(business.id))) ||
    null;

  const request: SearchRequest = {
    searchUrl: serverRuntimeConfig.searchUrl,
    businessRegistryUrl: serverRuntimeConfig.businessRegistry,
    geoInfo,
    sectors: [],
    types: [],
    trades:
      (filters.trade?.length > 100
        ? filters.trade.filter((t: any) => t.level === 'trade')
        : filters.trade
      )?.map((f) => f.key) ?? [],
    classifications: [],
    query: null,
    page: paginationDefaults.page,
    pageSize: paginationDefaults.pageSize,
    radius:
      context.locale === 'en-US'
        ? DEFAULT_SEARCH_RADIUS
        : DEFAULT_SEARCH_RADIUS_KM_TO_MI,
    businessFloor: 0,
    unit: DistanceUnit.Mile,
    sort: 'relevance',
    loggingContext: `ProfilePage - getServerSideProps relatedBusinesses: ${business.primarySlug}, ${business.id}`,
  };
  const relatedBusinesses: Business[] = (
    (await getSearchResults(request))?.results || []
  )
    .map((b) => convertUCSBusinessToBusiness(b))
    .filter((b) => b.id !== business.id);

  return {
    props: {
      geoInfo,
      business,
      relatedBusinesses,
      metrics,
      origin,
      claimBusinessProfile,
      adBanner,
      showLicenses,
    },
  };
}

export default function BusinessProfile({
  geoInfo,
  business,
  relatedBusinesses,
  metrics,
  origin,
  claimBusinessProfile,
  adBanner,
  showLicenses,
}: Readonly<{
  geoInfo: GeoInfo;
  business: Business;
  relatedBusinesses: Business[];
  metrics: Metrics;
  origin: string;
  claimBusinessProfile: boolean;
  adBanner: string;
  showLicenses: boolean;
}>) {
  const router = useRouter();
  const servicesProvidedMetaString = business.providedServices
    .map((s) => {
      return s.name;
    })
    .join(', ');
  const analyticsProps = {
    Name: business.name,
    Id: business.id,
    City: business.primaryAddress?.city,
    State: business.primaryAddress?.province,
    Services: servicesProvidedMetaString,
  };
  usePageView('Business Profile', analyticsProps);

  const classes = useStyles();

  const I18n = useI18nContext();
  const canonicalURL = canonicalUrl(router.asPath);
  const breadcrumbs: BreadcrumbItem[] = getLocationBreadcrumbs(geoInfo);
  const licenseArray = [];
  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.p.id.procore', {
            name: business.name,
            location: `${business.primaryAddress?.city}, ${business.primaryAddress?.province}`,
          })}
        </title>
        <meta
          name="description"
          content={`${business.name} is a construction company based in ${business.primaryAddress?.city}, ${business.primaryAddress?.province} and specializes in ${servicesProvidedMetaString}`}
        />
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <GoogleLocalBusinessScript businesses={[business]} />
      <GoogleBreadcrumbScript breadcrumbs={breadcrumbs} origin={origin} />
      <Grid container>
        <Header />
        <Grid
          justifyContent="center"
          container
          item
          component="section"
          className={classes.businessProfileLayout}
          role="main"
        >
          <Grid
            className={classes.innerGridContainer}
            component="section"
            container
            spacing={3}
            xs={12}
          >
            <Container className={classes.container}>
              <Box mt={4} mb={10}>
                <BusinessProfileLayout
                  business={business}
                  similarBusinesses={relatedBusinesses}
                  breadcrumbs={breadcrumbs}
                  metrics={metrics}
                  geoInfo={geoInfo}
                  claimBusinessProfile={claimBusinessProfile}
                  adBanner={adBanner}
                  licenses={showLicenses && licenseArray}
                />
              </Box>
            </Container>
          </Grid>
        </Grid>
        <Grid
          justifyContent="center"
          container
          item
          component="section"
          className={classes.otherTrades}
          role="complementary"
        >
          <Grid
            className={classes.innerGridContainer}
            component="section"
            container
            spacing={3}
            xs={12}
          >
            <Container className={classes.container}>
              <Box mt={6} mb={10}>
                <BrowseByTrade location={geoInfo} />
              </Box>
            </Container>
          </Grid>
        </Grid>
        <Footer />
      </Grid>
    </>
  );
}
